import { Control, Controller } from 'react-hook-form'
import { MultiSelectPanelHeaderTemplateParams } from 'primereact/multiselect'
import { useTranslation } from 'react-i18next'

// local imports
import DateUtil from 'utils/DateUtil'
import { IHealthLocationList } from 'types/HealthType'
import { GearFuelTypeOptions, IPoolVehicleListReq } from 'types/PoolVehicleType'
import DatePickerIcon from 'components/ui/DatePickerIcon'
import LocationMultiselect from 'components/ui/LocationMultiselect'
import { DatePickerWrapper, StyledDropdown, StyledMultiSelect } from './styled'

interface IPoolVehicleFormProps {
  control: Control<IPoolVehicleListReq>
  locationList: IHealthLocationList[] | undefined
  gearTypes: GearFuelTypeOptions[] | []
  fuelTypes: GearFuelTypeOptions[] | []
}

export default function PoolVehicleFilterForm({ control, locationList, fuelTypes, gearTypes }: IPoolVehicleFormProps) {
  const { t } = useTranslation()

  function panelHeaderTemplate(options: MultiSelectPanelHeaderTemplateParams) {
    return (
      <div className="p-multiselect-header">
        <label className="d-flex align-items-center">
          {options.checkboxElement}
          <span role="button" className="px-2 user-select-none">
            {t('buttons.choose-all')}
          </span>
        </label>
      </div>
    )
  }

  return (
    <>
      {/* Location */}
      <Controller
        name="locationIds"
        control={control}
        render={({ field }) => (
          <LocationMultiselect
            wrapperId="poolVehicleLocationWrapper"
            width="360px"
            selectProps={{
              id: 'poolVehicleLocationSelect',
              name: 'LocationIds',
              filterPlaceholder: t('most-used-words.search'),
              scrollHeight: '180px',
              maxSelectedLabels: 3,
              panelStyle: { width: '16rem', maxWidth: '400px' },
              placeholder: t('input-texts.choose-location'),
              emptyFilterMessage: t('most-used-words.option-not-found'),
              selectedItemsLabel: `${field.value?.length} ${t('input-texts.location-selected')}`,
              optionLabel: 'name',
              optionValue: 'id',
              options: locationList,
              value: field.value,
              onChange: (e) => field.onChange(e.value),
            }}
          />
        )}
      />

      {/* Reservation Date */}
      <DatePickerWrapper id="reservationDateWrapper">
        <Controller
          name="reservationDate"
          control={control}
          render={({ field }) => (
            <DatePickerIcon
              readOnlyInput={false}
              selectOtherMonths
              showOtherMonths
              disabledDays={[]}
              idName="poolVehicleReservationDate"
              defaultDate={field.value}
              minDate={new Date()}
              maxDate={DateUtil.futureMonth()}
              onSelectedDateChange={(e) => field.onChange(e.value as Date)}
              label={t('input-texts.reservation-date')}
              placeholder={t('input-texts.reservation-date')}
            />
          )}
        />
      </DatePickerWrapper>

      <Controller
        name="fuelTypeIds"
        control={control}
        render={({ field }) => (
          <StyledMultiSelect
            id={field.name}
            {...field}
            value={field.value}
            optionLabel="name"
            optionValue="id"
            name={field.name}
            options={fuelTypes}
            onChange={(e) => field.onChange(e.value)}
            placeholder={t('input-texts.fuel-type')}
            panelHeaderTemplate={panelHeaderTemplate}
            maxSelectedLabels={2}
            selectedItemsLabel={t('most-used-words.all-selected')}
          />
        )}
      />

      <Controller
        name="gearTypeId"
        control={control}
        render={({ field }) => (
          <StyledDropdown
            {...field}
            appendTo="self"
            value={field.value}
            onChange={(e) => field.onChange(e.value)}
            options={gearTypes}
            optionLabel="name"
            optionValue="id"
            placeholder={t('input-texts.gear-type')}
          />
        )}
      />
    </>
  )
}
